import { useEffect, useMemo, useRef, useState } from 'react'
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import { core } from '@musinsa/pbo-core'
import { notice } from '@/components/pbo/notice'
import { noticeIfUploadExceedsPage } from '@/hooks/useFetchAllRows'
import { callToAddImportLog } from '@/services/excel/apis'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { useIsMutating } from '@tanstack/react-query'
import { TABLE_STICKY_OFFSET } from '@/components/pbo/PboScope'
import { useCoreTableLayout } from '@/hooks/useCoreTableLayout'
import type { usePermissionSlot } from '@/hooks/useMenuPermission'
import BulkInputMenu, { type TableApi } from '@/components/bulkInput/BulkInputMenu'
import { GeneralInboundParams, GeneralInboundRow } from '../queries/useGetInboundList'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { NIKE_IDENTIFIERS_FLAG, type RequiredField, usesNikeIdentifiers } from '../queries/requiredFields'
import {
  asSkuUnmapped,
  downloadInboundsExcel,
  GR01_CONFIRM_MUTATION_KEY,
  hasMissingRequired,
  mixedShippers,
  SkuUnmappedError,
  useDeleteInbounds,
  useSaveInbounds,
  validateRows,
} from '../queries/useModifyInbounds'
import { BarcodePrintModal } from './BarcodePrintModal'
import { ExcelDownloadReasonModal } from './ExcelDownloadReasonModal'
import { ExcelUploadModal } from './ExcelUploadModal'
import { SkuUnmappedModal } from './SkuUnmappedModal'
import { parseInboundExcel } from './excelImport'
import { buildColumns, DELETE_IDS_MAX, GR01_PAGE_SIZE, BULK_ITEMS, GR07_GRID_CD, GR07_TABLE_ID, keepAlwaysRequired, toRequiredFields } from './consts'
import type { InboundRow } from './types'

/**
 * `__new` — pbo-core `core.Table` 의 신규 행 마커(런타임 계약, 컬럼 아님). 이 표시가 있는 행은 편집 전에도
 * 필수 컬럼 빈 칸을 검사해 행 배경을 오류색(`pbo-v2-error-row`)으로 칠한다. 조회 행은 사용자가 고친 뒤에만 칠한다.
 */
export type { InboundRow } from './types'

/**
 * 필수값 누락 행 배경(`pbo-v2-error-row`) — 표가 아니라 화면이 판정한다.
 *
 * DS 표의 자체 판정은 input·select·helper 셀만 세서 입고예정일자(datepicker)·수량(amountInput)이 비어도 행이 흰색이다.
 * DS 는 고치지 않기로 했다(사용자 결정 2026-10-01) — 소비 측 rowClassName 이 DS 판정을 통째로 대신하므로 여기서
 * 저장 전 검사(validateRows)와 같은 필수 목록·같은 빈 값 기준으로 칠한다. 대상은 DS 규칙 그대로 사용자가 만든 행(`__new`)과
 * 고친 행(`_edited`) — 서버가 준 빈 값은 사용자 잘못이 아니라 조회 직후에는 칠하지 않는다.
 */
const errorRowClassFor =
  (nikeIdentifiers: boolean, requiredFields: ReadonlyArray<RequiredField>) =>
  (row: InboundRow): string =>
    (row.__new || row._edited) && hasMissingRequired(row, nikeIdentifiers, requiredFields) ? 'pbo-v2-error-row' : ''

/**
 * 목록 검색은 화면에 보이는 컬럼만 훑는다.
 *
 * 행에는 표에 없는 필드도 실려 있다(입고유형 ORD_TYPE 등). 행 전체를 훑으면 사용자가 화면에서
 * 찾을 수 없는 값에 걸린 행이 남아, 왜 걸러졌는지 알 수 없는 결과가 나온다.
 */
const matchesQuery = (row: InboundRow, q: string, keys: string[]) =>
  !q || keys.some((key) => String(row[key as keyof InboundRow] ?? '').toLowerCase().includes(q.toLowerCase()))

// BE meta.message 우선, 빈 body 응답(게이트웨이/인프라 차단 등)은 HTTP 상태를 함께 노출
const apiErrMessage = (e: unknown) => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  if (resp?.data?.meta?.message) return resp.data.meta.message
  if (resp?.status) return `처리에 실패했습니다. (HTTP ${resp.status})`
  // 응답이 아니라 저장 전 가드가 던진 Error 도 여기로 온다(화주 혼재 등). response 가 없어서
  // 문구가 "처리에 실패했습니다." 로 뭉개지고 사용자는 왜 막혔는지 알 수 없었다.
  // index.tsx 에 같은 함수가 하나 더 있다 — 한쪽만 고치면 반쪽이 된다.
  if (e instanceof Error && e.message.trim()) return e.message
  return '처리에 실패했습니다.'
}

/** 엑셀 업로드로 들어온 신규 행의 빈 값 채우기 */
const EMPTY_ROW: Omit<GeneralInboundRow, '_id'> = {
  _shipperName: '',
  _isNew: true,
  SPR_NM: '',
  PLN_DATE: '',
  ORD_OPT_NO: '',
  ORD_TYPE: '',
  LOT_ATTR5: '',
  BRD_NM: '',
  GDS_GROUP1: '',
  GDS_NM: '',
  STL_NO: '',
  // 0 도 null 도 아니고 '' 다. 0 은 isBlank(0) 이 false 라 수량이 빈 행이 필수값 검사를 그냥 통과하고,
  // null 은 amountInput 렌더러가 입력창 대신 글자 '-' 를 돌려줘(pbo-core dist/index.mjs:6831,
  // `It = (t) => t == null`) 사용자가 그 셀을 고칠 수 없다. 저장은 '수량이 비어 있습니다' 로 막는데
  // 화면에서 값을 넣을 방법이 없어 막다른 길이 된다. '' 는 isBlank 가 true 라 방어는 그대로 걸리고
  // It('') 는 false 라 입력창이 뜬다.
  //
  // 이 자리에 null 이 남는 경로는 엑셀 업로드다. 수량 칸이 비고 그 오른쪽 컬럼까지 전부 빈 행이면
  // exceljs 의 eachCell({includeEmpty:true}) 이 그 행에 실재하는 마지막 셀까지만 돌아
  // (exceljs/lib/doc/row.js:135) 수량 셀을 아예 방문하지 않는다 — 파서가 키를 만들지 못해
  // 여기 기본값이 그대로 남는다.
  ORD_QTY: '',
  WH_NM: '',
  AP_PRICE: 0,
  WH_AREA: '',
  BARCODE: '',
  SKU_ID: '',
  UID: '',
  option_code: '',
  INS_DATE: '',
  INS_BY: '',
  UPD_DATE: '',
  UPD_BY: '',
  TRS_INVOICE_NO: '',
  COM_NM: '',
}

interface Props {
  rows: InboundRow[]
  setRows: (updater: (prev: InboundRow[]) => InboundRow[]) => void
  selected: string[]
  setSelected: (keys: string[]) => void
  searchParams: GeneralInboundParams
  shipperOptions: { value: string; label: string }[]
  /** 거래유형(LOT_ATTR5) 옵션 — GET /api/v1/combos 응답(LOGISPRD-12385). */
  transactionTypeOptions: { value: string; label: string }[]
  /** 센터명(WH_NM) 옵션 — GET /api/v1/combos 응답(LOGISPRD-12385). */
  whNmOptions: { value: string; label: string }[]
  /** 다운로드 사유코드(DOWNLOAD_CD) 옵션 — GET /api/v1/combos 응답(LOGISPRD-12385). */
  downloadReasonOptions: { value: string; label: string }[]
  /** 저장 성공 후 재조회 */
  onSaved: () => void
  /**
   * 저장·삭제·엑셀 업로드·다운로드 첫 줄 — 목록을 받는 중·일부 실패·조회 실패면 안내하고 false(useFetchAllRows).
   * 바코드 인쇄는 걸지 않는다 — 사람이 고른 행만 쓰므로 아직 받지 않은 행이 대상에 끼지 않는다.
   */
  guardAction: () => boolean
  /** 첫 구간을 받는 중 — 이때만 표를 막는다. 나머지 구간은 받는 대로 뒤에 붙는다. */
  loading?: boolean
  /** 목록 검색어 — URL 에 실려 탭을 다녀와도 유지된다(결정 12). 화면 로컬 state 로 두면 사라진다 */
  listQuery: string
  onListQueryChange: (value: string) => void
  /**
   * 부모가 [초기화]를 눌렀다는 신호. 값이 바뀌면 확정 전 입력(draft)을 비운다.
   * `listQuery` 만으로는 못 한다 — 이미 '' 인 상태에서 글자를 치고 초기화하면 listQuery 는 그대로라 effect 가 돌지 않아
   * 입력칸에 글자가 남고 목록은 안 걸러진 채 보인다(리뷰 지적 2026-09-11). 옛 코드는 key 리마운트로 비웠다.
   */
  resetToken?: number
  /**
   * 버튼 권한·화면관리 그리드 설정(LOGISPRD-17250) — 페이지가 부른 `usePermissionSlot('gr07', …)` 값.
   * 없으면(단위 테스트) 제한하지 않는다.
   */
  permission?: ReturnType<typeof usePermissionSlot>
}

export const GeneralInboundTable = ({
  rows,
  setRows,
  selected,
  setSelected,
  searchParams,
  shipperOptions,
  transactionTypeOptions,
  whNmOptions,
  downloadReasonOptions,
  onSaved,
  guardAction,
  loading,
  listQuery,
  onListQueryChange,
  resetToken,
  permission,
}: Readonly<Props>) => {
  // UID·옵션코드 복원(LOGISPRD-16423) kill switch — 조회 전·실패는 false(복원 전 동작)다. 표·필수검사·업로드·저장 본문이 같은 값을 본다
  const { data: nikeIdentifiers = false } = useFeatureFlag(NIKE_IDENTIFIERS_FLAG)
  const saveInbounds = useSaveInbounds(nikeIdentifiers)
  const deleteInbounds = useDeleteInbounds()

  // 렌더에 쓰이지 않고 행키 생성에만 쓴다. state 로 두면 같은 렌더 안에서 갱신 전 값을 읽어
  // 연속 업로드 시 행키가 겹친다.
  const importSeqRef = useRef(0)
  // 확정이 도는 중의 저장은 확정 대상 행을 바꾼다. 반대 방향은 index 에서 막는다.
  const isConfirming = useIsMutating({ mutationKey: GR01_CONFIRM_MUTATION_KEY }) > 0
  // 상태 기반 잠금이 켜지기 전 구간을 막는 ref 잠금 (index 의 확정 쪽과 같은 이유)
  const saveLock = useRef(false)
  // [일괄 입력] 이 체크 행에 값을 쓰는 표 API(LOGISPRD-15530)
  const tableApi = useRef<TableApi<InboundRow> | null>(null)
  const [printOpen, setPrintOpen] = useState(false)
  const [downloadOpen, setDownloadOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [skuError, setSkuError] = useState<SkuUnmappedError | null>(null)
  // 목록 검색 입력 draft — 확정 전 글자는 로컬에, 확정값(listQuery)이 밖에서 바뀌면(초기화·복원) 따라간다.
  // 확정은 Enter·돋보기·지우기에만 부모로 올린다 — 키 입력마다 URL·세션·선택해제가 돌지 않게.
  const [draft, setDraft] = useState(listQuery)
  useEffect(() => {
    setDraft(listQuery)
  }, [listQuery, resetToken])

  // 바코드 셀은 select 인데 pbo-core 렌더러가 variantProps.options 만 읽는다 — 안 주면 늘 빈 목록이다.
  // 현재 행의 고유값을 넣어 최소한 편집이 가능하게 한다.
  const barcodeOptions = useMemo(
    () => [...new Set(rows.map((r) => r.BARCODE).filter(Boolean))].map((v) => ({ value: v, label: v })),
    [rows],
  )
  // 엑셀 업로드·셀 편집은 조회 조건과 다른 화주코드를 행에 넣을 수 있다. 실제 행 중 하나라도
  // NIKE 식별자 정책 대상이면 필수값을 입력할 수 있도록 두 컬럼을 노출한다.
  const identifierShipperCode = useMemo(
    () => rows.find((row) => usesNikeIdentifiers(row.SPR_NM, nikeIdentifiers))?.SPR_NM ?? searchParams.shipperCode,
    [rows, searchParams.shipperCode, nikeIdentifiers],
  )
  // 화면관리 GR07_HEAD 설정 — 툴바·필수·[일괄 입력]. 셀 편집 잠금(editable=false)도 slot.columns 가 건다.
  const slot = permission?.forTable(GR07_TABLE_ID)
  const canShow = permission?.canShow ?? (() => true)
  const editable = permission?.editable ?? true
  const columns = useMemo(() => {
    const built = buildColumns(shipperOptions, barcodeOptions, transactionTypeOptions, whNmOptions, identifierShipperCode, nikeIdentifiers)
    // 공통 슬롯은 행 타입 없는 컬럼(CoreColumnType)을 받고 돌려준다 — 값은 같은 컬럼이라 이 표의 행 타입으로 되돌린다
    return slot ? keepAlwaysRequired(slot.columns(built as core.CoreColumnType[]) as core.CoreColumnType<InboundRow>[]) : built
  }, [slot, shipperOptions, barcodeOptions, transactionTypeOptions, whNmOptions, identifierShipperCode, nikeIdentifiers])
  // 저장 전 검사·빨간 행이 표 별표와 같은 필수 목록을 쓴다
  const requiredFields = useMemo(() => toRequiredFields(columns), [columns])
  const errorRowClass = useMemo(() => errorRowClassFor(nikeIdentifiers, requiredFields), [nikeIdentifiers, requiredFields])
  // 개인 컬럼 배치(순서·너비·고정) 서버 저장
  const columnLayout = useCoreTableLayout(GR07_GRID_CD, columns)
  const searchableKeys = useMemo(
    () => columns.map((c) => c.dataIndex).filter((d): d is string => typeof d === 'string' && Boolean(d)),
    [columns],
  )
  const filtered = useMemo(() => rows.filter((r) => matchesQuery(r, listQuery, searchableKeys)), [rows, listQuery, searchableKeys])
  const selectedRows = useMemo(() => rows.filter((r) => selected.includes(r._rowKey)), [rows, selected])

  /**
   * 편집 시 자동 체크 — 셀을 고친 행을 체크 선택에 더한다(컨덕터 표 dev.selectOnEdit · 1.0 CommonUtil.onCellValueChanged 의 setSelected(true) 미러).
   * [저장]·[확정]은 체크한 행만 보내므로, 고치고 체크를 잊으면 저장이 꺼진 채 남거나 고친 행이 빠진다.
   * 고친 행 = 지금 상태의 같은 키 행과 **값이 다른** 행(표시 마커 `_edited` 는 빼고 비교 — 참조만 보면 내용이 같은 재조회 뒤 전 행이 걸린다).
   * 더하기만 한다(사람이 푼 체크는 다시 고칠 때만 다시 켜진다). 행 수가 바뀐 변경(삭제)은 편집이 아니라 건너뛴다.
   * 일괄 적용 칸(collective)은 전 행이 바뀌므로 전 행이 체크된다 — 1.0 도 그 칸은 selectAll 이었다.
   */
  const autoSelectEdited = (next: InboundRow[]) => {
    if (next.length !== filtered.length) return
    const current = new Map(rows.map((r) => [r._rowKey, r]))
    const have = new Set(selected)
    const differs = (a: InboundRow, b: InboundRow) =>
      [...new Set([...Object.keys(a), ...Object.keys(b)])].some((k) => k !== '_edited' && !Object.is(a[k as keyof InboundRow], b[k as keyof InboundRow]))
    const added = next
      .filter((r) => {
        const cur = current.get(r._rowKey)
        return !!cur && cur !== r && differs(cur, r) && !have.has(r._rowKey)
      })
      .map((r) => r._rowKey)
    if (added.length) setSelected([...selected, ...added])
  }

  /**
   * 편집 셀 반영 — `core.Table` 은 편집 결과를 `onRowsChange(rows)` 로 **dataSource 전체**를 되돌려 준다.
   * dataSource 가 목록 검색으로 걸러진 부분집합이라, 통째로 갈아 끼우면 가려진 행이 사라진다.
   * 행키로 대조해 바뀐 행만 덮는다.
   *
   * SKU_ID 는 조회 응답에서만 채워지는 읽기 전용 칸이다. 바코드를 바꾼 행에 이전 바코드의 SKU_ID 가
   * 남으면 한 행에 서로 맞지 않는 두 값이 뜨므로 비운다. 저장 후 재조회 때 BE 값으로 다시 채워진다.
   */
  const onRowsChange = (next: InboundRow[]) => {
    const byKey = new Map(next.map((r) => [r._rowKey, r]))
    autoSelectEdited(next)
    setRows((prev) =>
      prev.map((r) => {
        const edited = byKey.get(r._rowKey)
        if (!edited || edited === r) return r
        // 고친 행 표시 — 빨간 행 판정 대상(errorRowClass). 재조회 행은 새 객체라 표시가 저절로 사라진다.
        return edited.BARCODE === r.BARCODE ? { ...edited, _edited: true } : { ...edited, SKU_ID: '', _edited: true }
      }),
    )
  }

  /**
   * 엑셀 업로드 — 1.0 TOOL_IMP 파리티: xlsx 클라 파싱 → 표에 반영, 사용자가 검토 후 [저장].
   *
   * 기존 행에 덧붙이지 않고 통째로 교체한다 (결정 09). 1.0 공용 툴바가
   * `gridApi.setRowData(result)` 다음 `gridApi.selectAll()` 이다(ToolBar.js:296-297).
   * 덧붙이면 같은 파일을 두 번 올렸을 때 신규 행이 두 벌 쌓이고 둘 다 저장에 실린다 —
   * 조회 행은 id 로 갱신이라 멱등하지만 신규 행은 id 가 없어 그대로 중복 생성된다.
   */
  const onImportFile = async (file: File) => {
    setUploadOpen(false)
    if (!guardAction()) return
    try {
      const imported = await parseInboundExcel(file, transactionTypeOptions, nikeIdentifiers)
      if (!imported.length) {
        notice.invalid('가져올 데이터 행이 없습니다.')
        return
      }
      const startSeq = importSeqRef.current
      importSeqRef.current += imported.length
      const fromFile = imported.map((item, i) => ({
        ...EMPTY_ROW,
        _id: 0,
        SPR_NM: searchParams.shipperCode ?? '',
        ...item,
        _rowKey: `imp-${startSeq + i}`,
        // 엑셀로 만든 행 = 사용자가 만든 신규 행 — 필수값이 빈 행을 바로 빨갛게 보여 준다(1.0 신규 행 필수 표기 파리티).
        __new: true,
      })) as InboundRow[]
      setRows(() => fromFile)
      callToAddImportLog({ menuCode: 'GR07', apiName: 'GR07_LIST_IMPORT', rowCount: fromFile.length })
      // 목록 검색어를 먼저 비운다. 검색어가 걸린 채로 올리면 표에는 걸러진 몇 행만 보이는데
      // 아래 selectAll 은 올린 전량을 선택해서, 화면에 없는 행까지 저장·확정 대상이 된다.
      // 업로드는 표의 내용을 갈아 끼우는 동작이라 표에 딸린 상태(검색어)도 같이 비우는 게 맞다.
      // 페이지 번호는 비울 것이 없다 — 이 화면은 전 페이지를 순회해 한 번에 받고 페이지네이션이
      // 클라이언트 쪽이라 조회 조건에 page 가 없다.
      setDraft('')
      onListQueryChange('')
      // 1.0 은 교체 직후 selectAll 이다 — 올린 행이 그대로 저장 대상이 된다.
      setSelected(fromFile.map((r) => r._rowKey))
      notice.success(`${imported.length}행을 가져왔습니다. 검토 후 저장하세요.`)
      // 표는 1만 행씩 나뉜다 — 넘으면 저장·확정이 현재 페이지 선택분만 나간다고 알린다.
      noticeIfUploadExceedsPage(fromFile.length)
    } catch (e) {
      notice.rowInvalid(e instanceof Error ? e.message : '엑셀 파일을 읽을 수 없습니다.')
    }
  }

  // [MOMS 공통] 저장 후 조회조건 리셋 금지 — 조건은 유지한 채 목록만 다시 읽는다.
  const onSave = async () => {
    if (saveLock.current || saveInbounds.isPending || isConfirming) return
    if (!guardAction()) return
    // 화주 혼재 차단 (결정 09) — 섞이면 화주별 순차 POST 라 중간 실패 시 부분 성공이 남고,
    // 재시도가 이미 들어간 신규 행을 한 번 더 만든다. 저장 전에 막는다.
    const mixed = mixedShippers(selectedRows)
    if (mixed.length) {
      notice.rowInvalid(`화주가 다른 행이 섞여 있습니다 (${mixed.join(', ')}). 화주별로 나눠 저장하세요.`)
      return
    }
    const invalid = validateRows(selectedRows, nikeIdentifiers, requiredFields)
    if (invalid.length) {
      // 행을 짚는 검증이라 알럿 — 앞 5건을 줄바꿈으로 한 알럿에 모은다(알럿 다섯 장을 겹쳐 쌓지 않게).
      const lines = invalid.slice(0, 5)
      if (invalid.length > 5) lines.push(`이 밖에 ${invalid.length - 5}건의 필수값 오류가 더 있습니다.`)
      notice.rowInvalid(lines.join('\n'))
      return
    }
    saveLock.current = true
    try {
      await saveInbounds.mutateAsync(selectedRows)
      notice.success(`${selectedRows.length}건 저장 완료`)
      setSelected([])
      onSaved()
    } catch (e) {
      // SKU 미매핑은 저장 전량 차단 — 바코드 목록을 따로 보여준다(1.0 SkuFailModal 파리티)
      const skuUnmapped = asSkuUnmapped(e)
      if (skuUnmapped) {
        setSkuError(skuUnmapped)
        return
      }
      notice.failure(apiErrMessage(e))
    } finally {
      saveLock.current = false
    }
  }

  // 삭제 — 미저장 신규 행(엑셀 업로드분)은 id 가 없어 표에서만 빼고, 저장된 행은 API 로 지운 뒤 재조회한다.
  const onDelete = async () => {
    if (saveLock.current || deleteInbounds.isPending) return
    if (!guardAction()) return
    const keys = new Set(selectedRows.map((r) => r._rowKey))
    const ids = selectedRows.filter((r) => !r._isNew).map((r) => r._id)
    saveLock.current = true
    try {
      if (ids.length) {
        const affected = await deleteInbounds.mutateAsync({ shipperCode: searchParams.shipperCode ?? '', ids })
        notice.success(`${affected}건 삭제 완료`)
        setSelected([])
        onSaved()
        return
      }
      setRows((prev) => prev.filter((r) => !keys.has(r._rowKey)))
      setSelected([])
      notice.success(`${keys.size}건 삭제 완료`)
    } catch (e) {
      notice.failure(apiErrMessage(e))
    } finally {
      saveLock.current = false
    }
  }

  // 삭제 컨펌 — 컨덕터 `core.ConfirmModal`(inboundDeleteConfirm) = 2.0 공용 확인창(PboConfirm).
  const openDeleteConfirm = () => {
    if (!guardAction()) return
    if (selectedRows.filter((r) => !r._isNew).length > DELETE_IDS_MAX) {
      notice.invalid(`삭제는 한 번에 ${DELETE_IDS_MAX.toLocaleString()}건까지 할 수 있습니다.`)
      return
    }
    // 문구는 컨덕터 inboundDeleteConfirm
    PboConfirm.show(`선택한 ${selected.length}건을 삭제하시겠습니까?`, { onConfirm: onDelete, okText: '삭제' })
  }

  const requireSelect = selected.length === 0

  return (
    <>
      {/* 컨덕터 `cmp.TableGroup`(`v2Skeleton.ts buildTableGroup`) 구조 그대로:
          TableSelectionScope > Flex(vertical 8) > TableHeader Flex(pad 4)[Space left · Space right] > core.Table.
          pbo-core 레거시(`Table`/PboTable·MomsActionBar·TableGroup)는 쓰지 않는다 — 컨덕터 그룹과 이름만 같은 통짜다. */}
      <core.TableSelectionScope>
        <core.Flex vertical gap={8} align="stretch">
          {/* 액션바 스티키 — 컨덕터 그룹 `actionBarSticky`(데모 규약, PboScope 에 CSS). 고정된 타이틀 아래에 붙고, 표 헤더는 TABLE_STICKY_OFFSET 으로 그 아래에 붙는다. */}
          <core.Flex className="pbo-v2-actionbar-sticky" style={{ padding: '4px 0' }} gap={8} justify="space-between" align="center">
            <core.Space>
              {/* 선택 건수 '선택 n / 전체' — 컨덕터 데모는 선택 스코프를 이어 보여 주는데 산출물엔 안 나간다(codegen gaps #24). 데모 파리티로 넘긴다.
                  헤더 필터로 가려진 행이 선택에 남아도 이 수로 드러난다(리뷰 지적 2026-09-11 — 헤더 필터 유지, 사용자 결정). */}
              <core.TableStat total={filtered.length} selectedCount={selected.length} />
              {/* 컨덕터 `inboundSaveButton` — primary(산출물 `type="primary"`). */}
              {canShow('SAVE') && (
                <core.Button type="primary" disabled={requireSelect || saveInbounds.isPending || deleteInbounds.isPending || isConfirming} onClick={onSave}>
                  저장
                </core.Button>
              )}
              {/* 확인 즉시 서버에서 지우는 [삭제] 라 툴바 remove 가 아니라 DELETE 로 판정한다(button-permission-code.md 규칙 5) */}
              {canShow('DELETE') && (
                <core.Button disabled={requireSelect || deleteInbounds.isPending || saveInbounds.isPending || isConfirming} onClick={openDeleteConfirm}>
                  삭제
                </core.Button>
              )}
              {canShow('PRINT') && (
                <core.Button disabled={requireSelect} onClick={() => setPrintOpen(true)}>
                  바코드 인쇄
                </core.Button>
              )}
              {editable && (
                <BulkInputMenu
                  apiRef={tableApi}
                  columns={columns}
                  rowKey="_rowKey"
                  items={slot ? slot.bulk(BULK_ITEMS) : BULK_ITEMS}
                />
              )}
            </core.Space>
            <core.Space>
              {/* 컨덕터 pagerSizeChanger='hide' = 전량(10,000) — '개씩' 셀렉트를 끈다. 페이지 이동은 이 화면에 없다(전 행 한 번에). */}
              <core.TablePager variant="paged" pageSize={GR01_PAGE_SIZE} total={filtered.length} showSizeChanger={false} />
              <core.Input.Search
                allowClear
                placeholder="목록 검색"
                style={{ width: 240 }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onSearch={onListQueryChange}
              />
              <span style={{ height: 18, margin: '0 4px', borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />
              {(slot?.toolbar.upload ?? true) && (
                <core.Button icon={<UploadOutlined />} onClick={() => guardAction() && setUploadOpen(true)}>
                  엑셀 업로드
                </core.Button>
              )}
              {/* 1.0 파리티 — 다운로드는 선택 무관 필터 전체(서버 xlsx, EXPORT 감사). 사유 모달 통과 후 실행 */}
              {(slot?.toolbar.download ?? true) && (
                <core.Button icon={<DownloadOutlined />} onClick={() => guardAction() && setDownloadOpen(true)}>
                  엑셀 다운로드
                </core.Button>
              )}
            </core.Space>
          </core.Flex>
          {/* No 순번 컬럼 없음 — 컨덕터 `inboundTable` 은 체크박스 다음이 바로 화주코드다. */}
          <core.Table<InboundRow>
            stickyOffset={TABLE_STICKY_OFFSET}
            columns={columns}
            dataSource={filtered}
            loading={loading}
            rowKey="_rowKey"
            apiRef={tableApi}
            size="middle"
            scroll={{ x: 'max-content' }}
            pagination={false}
            rowSelection={{
              type: 'checkbox',
              columnWidth: 48,
              selectedRowKeys: selected,
              onChange: (keys) => setSelected(keys.map(String)),
            }}
            onRowsChange={onRowsChange}
            rowClassName={errorRowClass}
            {...columnLayout}
          />
        </core.Flex>
      </core.TableSelectionScope>

      <ExcelDownloadReasonModal
        open={downloadOpen}
        reasonOptions={downloadReasonOptions}
        onCancel={() => setDownloadOpen(false)}
        onConfirm={async (reason) => {
          setDownloadOpen(false)
          if (!guardAction()) return
          try {
            await downloadInboundsExcel(searchParams, reason)
            notice.success('다운로드 완료')
          } catch (e) {
            notice.failure(apiErrMessage(e))
          }
        }}
      />
      <ExcelUploadModal open={uploadOpen} onCancel={() => setUploadOpen(false)} onUpload={onImportFile} />
      <BarcodePrintModal open={printOpen} rows={selectedRows} onClose={() => setPrintOpen(false)} />
      <SkuUnmappedModal error={skuError} onClose={() => setSkuError(null)} />
    </>
  )
}

export default GeneralInboundTable
